import React, { useEffect, useMemo, useState } from "react";

import { Play } from "lucide-react";

import Button from "../../Components/Button";
import DropdownSelector from "../../Components/DropdownSelector";
import CommonInput from "../../Components/Input";
import CommonModal from "../../Components/Modal";
import TextArea from "../../Components/TextArea";
import TruncatedTooltip from "../../Components/TruncatedTooltip";
import { useAppDispatch, useAppSelector } from "../../Store/Hooks";
import {
  createLibraryVideo,
  updateLibraryVideo,
  LibraryVideo,
  VideoTopic,
} from "../../Store/Reducers/VideoSlice";
import {
  extractVideoId,
  getYouTubeThumbnailUrl,
  isYouTubeUrl,
} from "../../Utils/Helper/videoUrl";

interface LibraryVideoFormModalProps {
  isOpen: boolean;
  video: LibraryVideo | null;
  onClose: () => void;
  onEditExisting: (video: LibraryVideo) => void;
}

const UNCATEGORIZED_OPTION = "";

const EMPTY_FORM = {
  title: "",
  video_url: "",
  description: "",
  topic_id: UNCATEGORIZED_OPTION,
};

const normalizeUrl = (url?: string | null) => (url || "").trim().toLowerCase();

/** Match stored/pasted URLs the same way for duplicate detection (incl. YouTube id). */
const urlsMatch = (a?: string | null, b?: string | null) => {
  const left = (a || "").trim();
  const right = (b || "").trim();
  if (!left || !right) return false;
  if (normalizeUrl(left) === normalizeUrl(right)) return true;
  const idA = extractVideoId(left);
  const idB = extractVideoId(right);
  return !!(idA && idB && idA === idB);
};

const bySortOrder = (a: LibraryVideo, b: LibraryVideo) =>
  (a.sort_order || 0) - (b.sort_order || 0) ||
  (a.title || "").localeCompare(b.title || "");

const LibraryVideoFormModal: React.FC<LibraryVideoFormModalProps> = ({
  isOpen,
  video,
  onClose,
  onEditExisting,
}) => {
  const dispatch = useAppDispatch();
  const {
    libraryVideos: items,
    topics,
    librarySaving: saving,
  } = useAppSelector(state => state.video);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState<Record<string, string>>({});
  /** URL when the modal opened (edit mode) — used to skip self-duplicate warnings. */
  const [originalVideoUrl, setOriginalVideoUrl] = useState("");
  /** 1-based insert position within the selected topic; default = end. */
  const [insertPosition, setInsertPosition] = useState<number | null>(null);
  const isEditMode = !!video;

  const topicPeers = useMemo(() => {
    const topicId = formData.topic_id || null;
    return (items as LibraryVideo[])
      .filter(item => (topicId ? item.topic_id === topicId : !item.topic_id))
      .filter(item => item.id !== video?.id)
      .sort(bySortOrder);
  }, [formData.topic_id, items, video?.id]);

  const peerCount = topicPeers.length;
  const defaultEndPosition = peerCount + 1;
  const selectedPosition = Math.min(
    Math.max(insertPosition ?? defaultEndPosition, 1),
    defaultEndPosition
  );
  const topicLabel =
    formData.topic_id === UNCATEGORIZED_OPTION
      ? "Uncategorized"
      : (topics as VideoTopic[]).find(topic => topic.id === formData.topic_id)
          ?.label || "Topic";

  useEffect(() => {
    if (!isOpen) return;
    const nextUrl = video?.video_url || "";
    setOriginalVideoUrl(nextUrl);
    setFormData(
      video
        ? {
            title: video.title || "",
            video_url: nextUrl,
            description: video.description || "",
            topic_id: video.topic_id || UNCATEGORIZED_OPTION,
          }
        : EMPTY_FORM
    );
    setErrors({});
    // Create: default to end. Edit: show current position within topic.
    if (video) {
      setInsertPosition(video.sort_order || null);
    } else {
      setInsertPosition(null);
    }
  }, [isOpen, video]);

  // Changing topic resets insert slot to end of destination.
  const handleTopicSelect = (value: string) => {
    setFormData(prev => ({ ...prev, topic_id: value }));
    setInsertPosition(null);
  };

  const topicOptions = useMemo(
    () => [
      { value: UNCATEGORIZED_OPTION, label: "Uncategorized" },
      ...(topics as VideoTopic[]).map(topic => ({
        value: topic.id,
        label: topic.label,
      })),
    ],
    [topics]
  );

  const duplicateVideo = useMemo(() => {
    const url = formData.video_url.trim();
    if (!url) return null;

    const editingId = video?.id;

    if (editingId && originalVideoUrl && urlsMatch(url, originalVideoUrl)) {
      return null;
    }

    return (
      (items as LibraryVideo[]).find(item => {
        if (editingId && String(item.id) === String(editingId)) return false;
        return urlsMatch(url, item.video_url);
      }) || null
    );
  }, [formData.video_url, items, video?.id, originalVideoUrl]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => {
        const next = { ...prev };
        delete next[name];
        return next;
      });
    }
  };

  const validate = () => {
    const nextErrors: Record<string, string> = {};
    if (!formData.title.trim()) nextErrors.title = "Title is required";
    if (!formData.video_url.trim()) {
      nextErrors.video_url = "Video URL is required";
    } else if (!isYouTubeUrl(formData.video_url.trim())) {
      nextErrors.video_url = "Please enter a valid YouTube URL";
    }
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    const payload = {
      title: formData.title.trim(),
      video_url: formData.video_url.trim(),
      description: formData.description.trim(),
      topic_id: formData.topic_id || null,
      ...(!isEditMode ? { sort_order: selectedPosition } : {}),
    };
    try {
      if (video) {
        await dispatch(
          updateLibraryVideo({ id: video.id, ...payload })
        ).unwrap();
      } else {
        await dispatch(createLibraryVideo(payload)).unwrap();
      }
      onClose();
    } catch {
      // Error toast is handled in VideoSlice.
    }
  };

  const renderInsertStrip = () => (
    <div
      className="library-insert-strip"
      role="group"
      aria-label="Insert position"
    >
      <div className="library-insert-strip__header">
        <div>
          <p className="library-insert-strip__title">{topicLabel}</p>
          <p className="library-insert-strip__meta">
            {peerCount === 0
              ? "No videos in this topic yet"
              : `${peerCount} video${peerCount === 1 ? "" : "s"} already`}
            {" · "}
            Will be position{" "}
            <strong>
              {selectedPosition} of {peerCount + 1}
            </strong>
          </p>
        </div>
      </div>

      <div className="library-insert-strip__track">
        {topicPeers.length === 0 ? (
          <button
            type="button"
            className={"library-insert-strip__slot is-selected"}
            onClick={() => setInsertPosition(1)}
          >
            Start of topic
          </button>
        ) : (
          <>
            {topicPeers.map((peer, index) => {
              const beforePos = index + 1;
              const thumb = getYouTubeThumbnailUrl(peer.video_url);
              return (
                <React.Fragment key={peer.id}>
                  <button
                    type="button"
                    className={`library-insert-strip__slot${
                      selectedPosition === beforePos ? " is-selected" : ""
                    }`}
                    onClick={() => setInsertPosition(beforePos)}
                    title={`Insert at position ${beforePos}`}
                  >
                    {beforePos === 1 ? "1 · start" : beforePos}
                  </button>
                  <div className="library-insert-strip__peer">
                    <span className="library-insert-strip__peer-order">
                      {index + 1}
                    </span>
                    <div className="library-insert-strip__peer-thumb">
                      {thumb ? (
                        <img src={thumb} alt="" />
                      ) : (
                        <Play size={14} className="text-gray-300" />
                      )}
                    </div>
                    <TruncatedTooltip
                      tooltip={peer.title || "Untitled Video"}
                      className="library-insert-strip__peer-title"
                    >
                      {peer.title || "Untitled Video"}
                    </TruncatedTooltip>
                  </div>
                </React.Fragment>
              );
            })}
            <button
              type="button"
              className={`library-insert-strip__slot${
                selectedPosition === defaultEndPosition ? " is-selected" : ""
              }`}
              onClick={() => setInsertPosition(defaultEndPosition)}
              title="Insert at end"
            >
              end
            </button>
          </>
        )}
      </div>
    </div>
  );

  return (
    <CommonModal
      isOpen={isOpen}
      toggle={onClose}
      title={isEditMode ? "Edit Library Video" : "Add Video to Library"}
      size="2xl"
      showHeader={true}
    >
      <div className="flex flex-col gap-5 mt-2">
        <CommonInput
          label="Video Title"
          name="title"
          value={formData.title}
          onChange={handleChange}
          placeholder="Enter video title"
          required
          error={errors.title}
          variant="field"
        />

        <div className="flex flex-col gap-2">
          <CommonInput
            label="Video URL"
            name="video_url"
            type="url"
            value={formData.video_url}
            onChange={handleChange}
            placeholder="https://www.youtube.com/watch?v=..."
            required
            error={errors.video_url}
            variant="field"
          />
          {duplicateVideo && (
            <div className="flex items-center justify-between gap-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2">
              <p className="text-xs text-amber-800">
                This URL is already in the library as &quot;
                {duplicateVideo.title || "Untitled Video"}&quot;.
              </p>
              <Button
                variant="secondary"
                onClick={() => onEditExisting(duplicateVideo)}
              >
                Edit existing
              </Button>
            </div>
          )}
        </div>

        <TextArea
          label="Description"
          name="description"
          value={formData.description}
          onChange={handleChange}
          placeholder="Enter video description"
          rows={3}
        />

        <div className="w-full">
          <DropdownSelector
            label="Topic"
            selected={formData.topic_id}
            options={topicOptions}
            onSelect={handleTopicSelect}
            triggerStyle="field"
            fullWidth
            enableScroll
          />
          <p className="mt-1 text-[12px] text-gray-500">
            Topics show as tabs on the home page video player. Manage them in
            the Topics tab.
          </p>
        </div>

        {!isEditMode && renderInsertStrip()}

        {isEditMode && (
          <div className="library-insert-strip library-insert-strip--readonly">
            <p className="library-insert-strip__meta">
              Current position in {topicLabel}:{" "}
              <strong>
                {video?.sort_order || "—"} of {peerCount + 1}
              </strong>
              . Changing topic places it at the end of the destination. Use Edit
              Order on the library to rearrange.
            </p>
          </div>
        )}

        {isEditMode && video.assigned_roles.length > 0 && (
          <p className="text-xs text-gray-500">
            Changes apply to every role this video is assigned to.
          </p>
        )}
      </div>

      <div className="flex justify-end gap-2 pt-4 border-t border-gray-200 mt-6">
        <Button variant="secondary" onClick={onClose}>
          Cancel
        </Button>
        <Button variant="primary" loading={saving} onClick={handleSubmit}>
          {isEditMode ? "Save Changes" : "Add to Library"}
        </Button>
      </div>
    </CommonModal>
  );
};

export default LibraryVideoFormModal;
